import { useRouter } from "@tanstack/react-router";
import { WifiOff } from "lucide-react";
import { useEffect } from "react";
import { type ConnectionStatus, reportPaxReachable } from "@/lib/network";

const RECONNECT_INTERVAL_MS = 3_000;
type OfflineReason = Exclude<ConnectionStatus, "online">;

export function OfflineStatus({ reason }: { reason: OfflineReason }) {
  const router = useRouter();
  const message =
    reason === "internet-unavailable"
      ? "You’re not connected to the internet. Pax will reconnect automatically when your connection returns."
      : "The Pax API server isn’t responding. Pax will reconnect automatically when it returns.";

  useEffect(() => {
    let disposed = false;
    let timeout: number;

    async function reconnectWhenReachable(): Promise<void> {
      const response = await fetch("/api/health", { cache: "no-store" }).catch(() => null);
      if (disposed) return;
      if (response?.ok) {
        reportPaxReachable();
        void router.invalidate();
        return;
      }
      timeout = window.setTimeout(reconnectWhenReachable, RECONNECT_INTERVAL_MS);
    }

    timeout = window.setTimeout(reconnectWhenReachable, RECONNECT_INTERVAL_MS);
    return () => {
      disposed = true;
      window.clearTimeout(timeout);
    };
  }, [router]);

  return (
    <main className="grid h-full min-h-80 place-items-center px-6 py-12 text-center">
      <div role="status" aria-live="polite">
        <WifiOff className="mx-auto size-6 text-muted-foreground" aria-hidden />
        <h1 className="mt-4 text-2xl font-semibold tracking-tight">You’re offline</h1>
        <p className="mt-3 text-sm text-muted-foreground">{message}</p>
      </div>
    </main>
  );
}
